import { cva, type VariantProps } from "class-variance-authority";
import { Slot as SlotPrimitive } from "radix-ui";
import type * as React from "react";
import { cn } from "@/lib/utils";

const listItemVariants = cva(
	"flex items-center gap-4 transition-[color] focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
	{
		variants: {
			size: {
				default: "p-4",
				dense: "p-2",
			},
			variant: {
				default: "",
				hover:
					"hover:bg-accent/80 dark:hover:bg-accent/20 hover:text-accent-foreground",
				card: "bg-background text-foreground border rounded-md not-last:mb-2",
				hoverCard:
					"bg-background text-foreground border rounded-md hover:bg-accent/80 dark:hover:bg-accent/20 hover:text-accent-foreground not-last:mb-2",
			},
		},
		defaultVariants: {
			size: "default",
			variant: "default",
		},
	},
);

interface ListRootProps extends React.HTMLAttributes<HTMLUListElement> {
	children: React.ReactNode;
	size?: "default" | "dense";
}

const ListRoot = ({
	className,
	size = "default",
	children,
	...props
}: ListRootProps) => {
	return (
		<ul
			data-size={size}
			className={cn("w-full divide-y divide-border rounded-md", className)}
			{...props}
		>
			{children}
		</ul>
	);
};
ListRoot.displayName = "List";

interface ListItemProps
	extends React.HTMLAttributes<HTMLLIElement>,
		VariantProps<typeof listItemVariants> {
	children: React.ReactNode;
	asChild?: boolean;
	ref?: React.Ref<HTMLLIElement>;
}

const ListItem = ({
	className,
	variant,
	asChild,
	size,
	children,
	...props
}: ListItemProps) => {
	const Comp = asChild ? SlotPrimitive.Slot : "li";
	return (
		<Comp
			data-slot="list-item"
			className={cn(listItemVariants({ variant, size, className }), {
				"cursor-pointer": !!props.onClick,
			})}
			{...props}
		>
			{children}
		</Comp>
	);
};
ListItem.displayName = "ListItem";

interface ListIconProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode;
}

const ListIcon = ({ className, children, ...props }: ListIconProps) => {
	return (
		<div
			className={cn("flex-shrink-0 text-accent-foreground", className)}
			{...props}
		>
			{children}
		</div>
	);
};
ListIcon.displayName = "ListIcon";

interface ListContentProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode;
}

const ListContent = ({ className, children, ...props }: ListContentProps) => {
	return (
		<div className={cn("flex flex-col flex-1 min-w-0", className)} {...props}>
			{children}
		</div>
	);
};
ListContent.displayName = "ListContent";

interface ListTitleProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode;
	size?: "default" | "dense";
}

const ListTitle = ({ className, children, size, ...props }: ListTitleProps) => {
	return (
		<div
			{...props}
			className={cn(
				"font-medium text-foreground",
				size === "dense" ? "text-xs" : "text-sm",
				className,
			)}
		>
			{children}
		</div>
	);
};
ListTitle.displayName = "ListTitle";

interface ListDescriptionProps
	extends React.HTMLAttributes<HTMLParagraphElement> {
	children: React.ReactNode;
	size?: "default" | "dense";
}

const ListDescription = ({
	className,
	children,
	size,
	...props
}: ListDescriptionProps) => {
	return (
		<div
			className={cn(
				"text-muted-foreground truncate mt-0.5",
				size === "dense" ? "text-xs" : "text-sm",
				className,
			)}
			{...props}
		>
			{children}
		</div>
	);
};
ListDescription.displayName = "ListDescription";

interface ListActionProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode;
}

const ListAction = ({ className, children, ...props }: ListActionProps) => {
	return (
		<div className={cn("flex-shrink-0 ml-auto", className)} {...props}>
			{children}
		</div>
	);
};
ListAction.displayName = "ListAction";

export {
	ListAction,
	ListContent,
	ListDescription,
	ListIcon,
	ListItem,
	ListRoot,
	ListTitle,
};
